/* eslint-disable @typescript-eslint/no-var-requires */
const defaultTheme = require('tailwindcss/defaultTheme')
const colors = require('tailwindcss/colors')

module.exports = {
  content: ['./src/**/*.{js,jsx,ts,tsx,css}'],
  // v3: `false` is no longer a distinct option and behaves like 'media'.
  // No `dark:` variant classes exist in this codebase, so this is a no-op.
  darkMode: 'media',
  theme: {
    fontSize: {
      ...defaultTheme.fontSize,
      '2xs': '.625rem',
      xsm: '13px',
    },
    scale: {
      '37-5': '.375',
      ...defaultTheme.scale,
    },
    screens: {
      xs: '475px',
      ...defaultTheme.screens,
    },
    extend: {
      animation: {
        'rotate-center': 'rotate-center 1s linear infinite reverse both',
      },
      borderWidth: {
        1: '1px',
        3: '3px',
      },
      colors: {
        // Tailwind v3 stopped aliasing these to the extended palette by
        // default (v2: green->emerald, yellow->amber, purple->violet).
        // Keep the aliases so existing green-*/yellow-*/purple-* usages
        // render identically to v2.
        green: colors.emerald,
        yellow: colors.amber,
        purple: colors.violet,
        brand: {
          accent1: '#f9f871',
          accent2: '#21B0B8',
          accent3: '#80E49D',
          accent4: '#008fdc',
          accent5: '#73B06F',
          main: '#9489fa',
        },
        error: '#AD3939',
        folder: '#FFDC70',
      },
      keyframes: {
        'rotate-center': {
          '0%': {
            transform: 'rotate(0)',
          },
          to: {
            transform: 'rotate(360deg)',
          },
        },
      },
      // New design-system semantic tokens (see src/styles/globals.css).
      // Class names mirror the CSS variable names 1:1 for discoverability;
      // kept separate from the `colors` block above so existing utilities
      // (bg-purple-500, text-green-600, etc.) keep rendering as before.
      backgroundColor: {
        'surface-accent-default': 'var(--surface-accent-default)',
        'surface-accent-default-hover': 'var(--surface-accent-default-hover)',
        'surface-accent-default-pressed':
          'var(--surface-accent-default-pressed)',
        'surface-accent-subtle': 'var(--surface-accent-subtle)',
        'surface-accent-subtle-hover': 'var(--surface-accent-subtle-hover)',
        'surface-accent-subtle-pressed': 'var(--surface-accent-subtle-pressed)',
        'surface-base': 'var(--surface-base)',
        'surface-brand-primary-default': 'var(--surface-brand-primary-default)',
        'surface-brand-primary-hover': 'var(--surface-brand-primary-hover)',
        'surface-brand-primary-pressed': 'var(--surface-brand-primary-pressed)',
        'surface-brand-primary-subtle': 'var(--surface-brand-primary-subtle)',
        'surface-brand-secondary-default':
          'var(--surface-brand-secondary-default)',
        'surface-brand-secondary-hover': 'var(--surface-brand-secondary-hover)',
        'surface-brand-secondary-pressed':
          'var(--surface-brand-secondary-pressed)',
        'surface-brand-tertiary-default':
          'var(--surface-brand-tertiary-default)',
        'surface-brand-tertiary-hover': 'var(--surface-brand-tertiary-hover)',
        'surface-brand-tertiary-pressed':
          'var(--surface-brand-tertiary-pressed)',
        'surface-default': 'var(--surface-default)',
        'surface-default-hover': 'var(--surface-default-hover)',
        'surface-default-pressed': 'var(--surface-default-pressed)',
        'surface-disabled-default': 'var(--surface-disabled-default)',
        'surface-disabled-subtle': 'var(--surface-disabled-subtle)',
        'surface-error-default': 'var(--surface-error-default)',
        'surface-error-default-hover': 'var(--surface-error-default-hover)',
        'surface-error-default-pressed': 'var(--surface-error-default-pressed)',
        'surface-error-subtle': 'var(--surface-error-subtle)',
        'surface-error-subtle-hover': 'var(--surface-error-subtle-hover)',
        'surface-error-subtle-pressed': 'var(--surface-error-subtle-pressed)',
        'surface-information-default': 'var(--surface-information-default)',
        'surface-information-default-hover':
          'var(--surface-information-default-hover)',
        'surface-information-default-pressed':
          'var(--surface-information-default-pressed)',
        'surface-information-subtle': 'var(--surface-information-subtle)',
        'surface-information-subtle-hover':
          'var(--surface-information-subtle-hover)',
        'surface-information-subtle-pressed':
          'var(--surface-information-subtle-pressed)',
        'surface-neutral-default': 'var(--surface-neutral-default)',
        'surface-neutral-default-hover': 'var(--surface-neutral-default-hover)',
        'surface-neutral-default-pressed':
          'var(--surface-neutral-default-pressed)',
        'surface-neutral-default-subtle-hover':
          'var(--surface-neutral-default-subtle-hover)',
        'surface-neutral-default-subtle-pressed':
          'var(--surface-neutral-default-subtle-pressed)',
        'surface-neutral-subtle': 'var(--surface-neutral-subtle)',
        'surface-neutral-subtle-hover': 'var(--surface-neutral-subtle-hover)',
        'surface-neutral-subtle-pressed':
          'var(--surface-neutral-subtle-pressed)',
        'surface-overlay': 'var(--surface-overlay)',
        'surface-page': 'var(--surface-page)',
        'surface-page-secondary': 'var(--surface-page-secondary)',
        'surface-secondary': 'var(--surface-secondary)',
        'surface-success-default': 'var(--surface-success-default)',
        'surface-success-default-hover': 'var(--surface-success-default-hover)',
        'surface-success-default-pressed':
          'var(--surface-success-default-pressed)',
        'surface-success-subtle': 'var(--surface-success-subtle)',
        'surface-success-subtle-hover': 'var(--surface-success-subtle-hover)',
        'surface-success-subtle-pressed':
          'var(--surface-success-subtle-pressed)',
        'surface-warning-default': 'var(--surface-warning-default)',
        'surface-warning-default-hover': 'var(--surface-warning-default-hover)',
        'surface-warning-default-pressed':
          'var(--surface-warning-default-pressed)',
        'surface-warning-subtle': 'var(--surface-warning-subtle)',
        'surface-warning-subtle-hover': 'var(--surface-warning-subtle-hover)',
        'surface-warning-subtle-pressed':
          'var(--surface-warning-subtle-pressed)',
      },
      textColor: {
        action: 'var(--text-action)',
        'brand-on-primary-subtle': 'var(--text-brand-on-primary-subtle)',
        'brand-on-secondary': 'var(--text-brand-on-secondary)',
        'brand-primary': 'var(--text-brand-primary)',
        'brand-secondary': 'var(--text-brand-secondary)',
        'brand-tertiary': 'var(--text-brand-tertiary)',
        'disabled-default': 'var(--text-disabled-default)',
        'error-on-color': 'var(--text-error-on-color)',
        'error-on-subtle': 'var(--text-error-on-subtle)',
        'information-on-color': 'var(--text-information-on-color)',
        'information-on-subtle': 'var(--text-information-on-subtle)',
        'neutral-default': 'var(--text-neutral-default)',
        'neutral-default-hover': 'var(--text-neutral-default-hover)',
        'primary-default': 'var(--text-primary-default)',
        'primary-default-hover': 'var(--text-primary-default-hover)',
        'primary-on-color': 'var(--text-primary-on-color)',
        'primary-on-color-hover': 'var(--text-primary-on-color-hover)',
        'success-on-color': 'var(--text-success-on-color)',
        'success-on-subtle': 'var(--text-success-on-subtle)',
        'warning-on-color': 'var(--text-warning-on-color)',
        'warning-on-subtle': 'var(--text-warning-on-subtle)',
        // icon tokens, nested so they read as `text-icon-*` (icons are
        // colored via `text-*`/`currentColor` in this codebase's SVGs)
        icon: {
          'brand-on-primary-subtle': 'var(--icon-brand-on-primary-subtle)',
          'brand-on-secondary': 'var(--icon-brand-on-secondary)',
          'brand-primary': 'var(--icon-brand-primary)',
          'brand-secondary': 'var(--icon-brand-secondary)',
          'brand-tertiary': 'var(--icon-brand-tertiary)',
          'error-default': 'var(--icon-error-default)',
          'error-default-hover': 'var(--icon-error-default-hover)',
          'error-on-color': 'var(--icon-error-on-color)',
          'error-on-color-hover': 'var(--icon-error-on-color-hover)',
          'error-on-subtle': 'var(--icon-error-on-subtle)',
          'information-default': 'var(--icon-information-default)',
          'information-default-hover': 'var(--icon-information-default-hover)',
          'information-on-color': 'var(--icon-information-on-color)',
          'information-on-color-hover':
            'var(--icon-information-on-color-hover)',
          'information-on-subtle': 'var(--icon-information-on-subtle)',
          'neutral-default': 'var(--icon-neutral-default)',
          'neutral-default-hover': 'var(--icon-neutral-default-hover)',
          'primary-default': 'var(--icon-primary-default)',
          'primary-default-hover': 'var(--icon-primary-default-hover)',
          'primary-on-color': 'var(--icon-primary-on-color)',
          'primary-on-color-hover': 'var(--icon-primary-on-color-hover)',
          'success-default': 'var(--icon-success-default)',
          'success-default-hover': 'var(--icon-success-default-hover)',
          'success-on-color': 'var(--icon-success-on-color)',
          'success-on-color-hover': 'var(--icon-success-on-color-hover)',
          'success-on-subtle': 'var(--icon-success-on-subtle)',
          'warning-default': 'var(--icon-warning-default)',
          'warning-default-hover': 'var(--icon-warning-default-hover)',
          'warning-on-color': 'var(--icon-warning-on-color)',
          'warning-on-color-hover': 'var(--icon-warning-on-color-hover)',
          'warning-on-subtle': 'var(--icon-warning-on-subtle)',
        },
      },
      borderColor: {
        'brand-primary': 'var(--border-brand-primary)',
        'brand-secondary': 'var(--border-brand-secondary)',
        'brand-tertiary': 'var(--border-brand-tertiary)',
        'disabled-default': 'var(--border-disabled-default)',
        'error-default': 'var(--border-error-default)',
        'error-default-hover': 'var(--border-error-default-hover)',
        'error-default-subtle': 'var(--border-error-default-subtle)',
        'error-default-subtle-hover':
          'var(--border-error-default-subtle-hover)',
        'error-focus': 'var(--border-error-focus)',
        'information-default': 'var(--border-information-default)',
        'information-default-hover': 'var(--border-information-default-hover)',
        'information-default-subtle':
          'var(--border-information-default-subtle)',
        'information-default-subtle-hover':
          'var(--border-information-default-subtle-hover)',
        'information-focus': 'var(--border-information-focus)',
        'neutral-default': 'var(--border-neutral-default)',
        'neutral-default-hover': 'var(--border-neutral-default-hover)',
        'primary-default': 'var(--border-primary-default)',
        'primary-default-hover': 'var(--border-primary-default-hover)',
        'primary-default-subtle': 'var(--border-primary-default-subtle)',
        'primary-default-subtle-hover':
          'var(--border-primary-default-subtle-hover)',
        'primary-focus': 'var(--border-primary-focus)',
        'success-default': 'var(--border-success-default)',
        'success-default-hover': 'var(--border-success-default-hover)',
        'success-default-subtle': 'var(--border-success-default-subtle)',
        'success-default-subtle-hover':
          'var(--border-success-default-subtle-hover)',
        'success-focus': 'var(--border-success-focus)',
        'warning-default': 'var(--border-warning-default)',
        'warning-default-hover': 'var(--border-warning-default-hover)',
        'warning-default-subtle': 'var(--border-warning-default-subtle)',
        'warning-default-subtle-hover':
          'var(--border-warning-default-subtle-hover)',
        'warning-focus': 'var(--border-warning-focus)',
      },
      // Text-style tokens (see the `--heading-*`/`--body`/etc. shorthands in
      // src/styles/globals.css), consumed by the matchComponents plugin below.
      // Grouped per component name (heading/body/...) because matchComponents
      // names generated classes `${component}-${key}` (or bare `${component}`
      // for a `DEFAULT` key) — nesting per group is what yields `.heading-1`,
      // `.body`, `.body-strong`, etc. instead of Tailwind's usual dashed scale.
      textStyle: {
        heading: {
          1: {
            font: 'var(--heading-1)',
            letterSpacing: 'var(--heading-1-letter-spacing)',
          },
          2: {
            font: 'var(--heading-2)',
            letterSpacing: 'var(--heading-2-letter-spacing)',
          },
          3: {
            font: 'var(--heading-3)',
            letterSpacing: 'var(--heading-3-letter-spacing)',
          },
          4: {
            font: 'var(--heading-4)',
            letterSpacing: 'var(--heading-4-letter-spacing)',
          },
        },
        body: {
          DEFAULT: {
            font: 'var(--body)',
            letterSpacing: 'var(--body-letter-spacing)',
          },
          strong: {
            font: 'var(--body-strong)',
            letterSpacing: 'var(--body-strong-letter-spacing)',
          },
        },
        caption: {
          DEFAULT: {
            font: 'var(--caption)',
            letterSpacing: 'var(--caption-letter-spacing)',
          },
        },
        code: {
          DEFAULT: {
            font: 'var(--code)',
            letterSpacing: 'var(--code-letter-spacing)',
          },
        },
        quote: {
          DEFAULT: {
            font: 'var(--quote)',
            letterSpacing: 'var(--quote-letter-spacing)',
          },
        },
        label: {
          DEFAULT: {
            font: 'var(--label)',
            letterSpacing: 'var(--label-letter-spacing)',
          },
        },
      },
      height: {
        9.5: '2.375rem',
        18: '4.5rem',
        22: '90px',
        v65: '65vh',
      },
      width: {
        9.5: '2.375rem',
        22: '90px',
        62: '246px',
      },
      minWidth: {
        10: '2.5rem',
        24: '6rem',
      },
      padding: {
        2.25: '0.563rem',
      },
      boxShadow: {
        lg2: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 0px 15px -3px rgba(0, 0, 0, 0.1)',
      },
      flex: {
        80: '1 0 90%',
      },
    },
  },
  plugins: [
    require('@tailwindcss/forms')({
      strategy: 'class',
    }),
    // Generates .heading-1..4, .body, .body-strong, .caption, .code, .quote,
    // .label from `theme.extend.textStyle` (unused unless applied in JSX, so
    // this has no effect on existing markup).
    function ({ matchComponents, theme }) {
      Object.keys(theme('textStyle')).forEach((name) => {
        matchComponents(
          { [name]: (value) => value },
          { values: theme(`textStyle.${name}`) }
        )
      })
    },
  ],
}
